<template>
    <div id="draft-list" class="page-content">
        <!-- Spinner On load -->
        <spinner v-if="loading" class="spinner" />

        <!-- If no drafts -->
        <p v-if="!loading && drafts.length == 0" class="empty-message">
            {{ $t('drafts.none') }}
        </p>

        <!-- Draft items, one per conversation -->
        <transition-group name="flip-list" tag="div">
            <component :is="'DraftItem'" v-for="draft in drafts" :key="draft.conversation_id" :draft-data="draft" @deleted="removeDraft" />
        </transition-group>
    </div>
</template>

<script>

import { Api, SessionCache } from '@/utils';
import DraftItem from './DraftItem.vue';
import Spinner from '@/components/Spinner.vue';

export default {
    name: 'Drafts',

    components: {
        DraftItem,
        Spinner
    },

    data () {
        return {
            title: this.$t('drafts.title'),
            drafts: [],
            loading: true,
        };
    },

    mounted () {
        this.$store.state.msgbus.$on('refresh-btn', this.refresh);

        this.fetchDrafts();

        // Construct colors object from saved global theme
        const colors = {
            'default': this.$store.state.theme_global_default,
            'dark': this.$store.state.theme_global_dark,
            'accent': this.$store.state.theme_global_accent,
        };

        // Commit them to current application colors
        this.$store.commit('colors', colors);
    },

    beforeDestroy () {
        this.$store.state.msgbus.$off('refresh-btn', this.refresh);
    },

    methods: {

        fetchDrafts () {
            this.loading = true;

            Api.drafts.get()
                .then(response => this.processDrafts(response))
                .catch(() => this.processDrafts([]));
        },

        /**
         * Groups drafts by conversation. The server can only delete all of a conversation's drafts
         * at once (drafts/remove/:device_conversation_id), and a conversation can end up with the
         * same draft saved more than once, so one row per conversation with each distinct text once.
         */
        processDrafts (response) {
            const groups = {};
            const renderList = [];

            for (const draft of response) {
                if (!draft) {
                    continue;
                }

                const id = draft.device_conversation_id;
                let group = groups[id];
                if (!group) {
                    group = groups[id] = this.createGroup(id);
                    renderList.push(group);
                }

                const mimeType = draft.mime_type || '';
                if (mimeType.indexOf('text/plain') === 0) {
                    if (draft.data && group.texts.indexOf(draft.data) === -1) {
                        group.texts.push(draft.data);
                    }
                } else if (group.media.indexOf(mimeType) === -1) {
                    group.media.push(mimeType);
                }
            }

            this.drafts = renderList;
            this.loading = false;

            this.$store.commit("loading", false);
            this.$store.commit('title', this.title);

            this.fetchMissingTitles();
        },

        createGroup (conversationId) {
            const conversation = SessionCache.getConversation(conversationId);

            return {
                conversation_id: conversationId,
                title: conversation ? conversation.title : null,
                read: conversation ? conversation.read : undefined,
                archive: conversation ? conversation.archive : false,
                texts: [],
                media: []
            };
        },

        // Conversations that aren't in the cached lists (e.g. archived ones that were never opened).
        fetchMissingTitles () {
            for (const group of this.drafts) {
                if (group.title !== null) {
                    continue;
                }

                Api.conversations.getById(group.conversation_id)
                    .then(conversation => {
                        group.title = conversation.title;
                        group.read = conversation.read;
                        group.archive = conversation.archive;
                    })
                    .catch(() => { }); // Keeps the "Unknown Conversation" fallback
            }
        },

        removeDraft (conversationId) {
            this.drafts = this.drafts.filter(draft => draft.conversation_id !== conversationId);
        },

        refresh () {
            this.fetchDrafts();
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
    @import "../../../assets/scss/_vars.scss";

    #draft-list {
        width: 100%;

        .spinner {
            margin-top: 100px;
        }
    }

    .empty-message {
        color: rgba(0, 0, 0, 0.54);
        margin-top: 100px;
        text-align: center;
    }

    .flip-list-enter, .flip-list-leave-to  {
        opacity: 0;
    }

    .flip-list-leave-active {
        position: absolute;
    }

    .flip-list-move {
        transition: transform $anim-time;
    }
</style>
